import React, {Component} from 'react'
import propTypes from 'prop-types'
import {isEmpty, isNil} from 'lodash'
import {IconButton} from 'material-ui'
import ListSubheader from '@material-ui/core/ListSubheader'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import theme from 'theme/index'
import iconNames from '../../../theme/icons'
import Button from '../../../components/button'
import Popover from '../../../components/popover'
import Icon from '../../../components/icon'
import SearchBar from '../../../components/searchBar'
import MenuButton from '../../../components/menuButton'
import {MultiSelectField} from 'components/selectField'
import {prefix} from 'helpers/component'
import {t} from "helpers/language"

const iconSize = 16
const getClassName = prefix('pivotTable-header-')

class Header extends Component {
  constructor(props) {
    super(props)
    this.state = {
      showFilters: false,
      showSort: false,
      filterAnchor: null,
      sortAnchor: null,
    }
  }

  search = () => {
    this.searchBar.focus()
  }

  showFilters = (e) => {
    this.setState({
      showFilters: true,
      filterAnchor: e.currentTarget,
    })
  }

  hideFilters = () => {
    this.setState({
      showFilters: false,
    })
  }

  groupsChanged = (groups) => {
    this.props.onGroupsChanged(groups.map(g => g.value))
  }

  sortColumnsByName = (a, b) => {
    const labelA = a.label || a.alias
    const labelB = b.label || b.alias
    if (labelA < labelB) return -1
    if (labelA > labelB) return 1
    return 0
  }

  translateActions = (actions, showDivider) => {
    if (!isEmpty(actions)) {
      return [
        showDivider
          ? <div key="_" className={getClassName('divider')}/>
          : null,
        ...actions.map((action, index) => {

          if (action.divider) {
            return <div key={index} className={getClassName('divider')}/>
          }

          if (action.isMenu) {
            return (
              <MenuButton
                key={index}
                className={getClassName(action.label ? 'button' : 'iconButton')}
                flat
                size={iconSize}
                {...action}
              />
            )
          }

          if (action.element) {
            return action.element
          }

          const buttonClass =
            `${getClassName(action.label ? 'button' : 'iconButton')} ${action.icon} ${action.className}`

          return (
            <Button
              key={index}
              noFill={action.noFill}
              className={buttonClass}
              flat={true}
              size={iconSize}
              icon={action.icon}
              iconColor={theme.palette.primaryIconColor}
              onClick={action.onClick}
              disabled={action.disabled}
              tooltip={action.tooltip}
              label={action.label}
              labelPosition={action.labelPosition}
            />
          )
        }),
      ].filter(e => !!e)
    }
  }

  render() {
    const {
      onSearched,
      singularNoun,
      stickyTop,
      allColumns,
      actions,
      groups,
      searchingAllowed,
      children,
      groupingAllowed,
      onGroupsChanged,
      showAdd,
      search,
      onAdd,
      onBulkRemove,
      onExport,
      onRefresh,
      isAddButtonDisabled,
      isGroupByEnabled,
      tightFilters,
    } = this.props
    let {addLabel, searchPlaceholder} = this.props
    const {showFilters, filterAnchor} = this.state

    const sortedColumns = Object.keys(allColumns).map(k => allColumns[k])
    sortedColumns.sort(this.sortColumnsByName)

    let groupBar
    if (groupingAllowed && onGroupsChanged) {

      let groupOptions = sortedColumns.map(c => ({
        value: c.key,
        label: c.label || c.alias,
      }))

      const groupValue = []
      groups.forEach(group => {
        const option = groupOptions.find(option => option.value === group)
        if (option) groupValue.push(option)
      })

      if (groups.length >= 5) {
        groupOptions = [{
          value: null,
          label: t('Remove a field to group by something else'),
          disabled: true,
        }]
      }

      groupBar = isGroupByEnabled && (
        <div className={getClassName('groupBar')}>
          <MultiSelectField
            placeholder={t(`Group by`)}
            options={groupOptions}
            value={groupValue}
            flat={true}
            isOnlySearchLabelProperty={true}
            onChange={this.groupsChanged}
          />
        </div>
      )
    }

    let exportButton
    if (onExport) {
      exportButton = (
        <IconButton
          tooltip={t("Export")}
          className={getClassName(['iconButton', 'exportButton'])}
          onClick={onExport}>
          <Icon name={iconNames.export} size={iconSize}/>
        </IconButton>
      )
    }

    let refreshButton
    if (onRefresh) {
      refreshButton = (
        <IconButton
          tooltip={t("Refresh")}
          className={getClassName(['iconButton', 'refreshButton'])}
          onClick={onRefresh}>
          <Icon name={iconNames.refresh} size={iconSize}/>
        </IconButton>
      )
    }

    let filterButton, filterMenu
    if (children) {
      filterButton = (
        <IconButton
          tooltip={t("Filter")}
          className={getClassName(['iconButton', 'filterButton'])}
          onClick={this.showFilters}
        >
          <Icon name="filter_filled" size={iconSize}/>
        </IconButton>
      )

      const filterItems = React.Children.map(
        this.props.children,
        function (filterItem, index) {
          return (
            <ListItem
              key={'filterItem_' + index}
              style={tightFilters ? {paddingTop: 0, paddingBottom: 0} : undefined}
            >
              {filterItem}
            </ListItem>
          )
        }
      )

      const baseFilterMenuHeight = 48 // header height
      const filterItemHeight = 79 // item with padding height
      const filterMenuHeight = baseFilterMenuHeight + (filterItemHeight * filterItems.length)

      filterMenu = (
        <Popover
          open={showFilters}
          className={getClassName('filter-popover')}
          padded={false}
          anchor={filterAnchor}
          onRequestClose={this.hideFilters}
        >
          <List style={{height: filterMenuHeight, width: 294}}>
            <ListSubheader style={{paddingLeft: '16px'}}>
              {t('Filters')}
            </ListSubheader>
            {filterItems}
          </List>
        </Popover>
      )
    }

    let bulkRemoveButton
    if (onBulkRemove) {
      bulkRemoveButton = (
        <IconButton
          tooltip={t("Close / Reject Quotations")}
          className={getClassName(['iconButton', 'bulkRemoveButton'])}
          onClick={onBulkRemove}
        >
          <Icon
            backgroundColor={theme.palette.primaryIconColor}
            color={'white'}
            name={iconNames.clear}
            size={iconSize}
            type={'CIRCLE'}
          />
        </IconButton>
      )
    }

    let searchBar
    if (searchingAllowed) {
      const searchClasses = ['search']
      if (groupingAllowed && onGroupsChanged) searchClasses.push('search-withGroups')
      if (isNil(searchPlaceholder)) searchPlaceholder = 'Search'

      searchBar = (
        <div className={getClassName(searchClasses)}>
          <SearchBar
            ref={e => {
              this.searchBar = e
            }}
            placeholder={searchPlaceholder}
            autoFocus={false}
            value={search}
            onSearch={onSearched}
            className={getClassName('searchBar')}/>
        </div>
      )
    }

    let addButton
    if (showAdd && onAdd) {
      if (isNil(addLabel)) {
        addLabel = `New ${singularNoun}`
      }
      addButton = (
        <Button
          className={getClassName('addButton')}
          icon={theme.icons.addNoMargin}
          size={12}
          primary
          label={addLabel}
          disabled={isAddButtonDisabled}
          onClick={onAdd}
        />
      )
    }

    const showActionDivider = (addButton || filterButton || exportButton || refreshButton)
    const actionButtons = this.translateActions(actions, showActionDivider)

    let containerStyle
    const containerClasses = ['container']

    if (!isNil(stickyTop)) {
      containerClasses.push('container-sticky')
      containerStyle = {top: stickyTop}
    }

    let buttons
    if (addButton || actionButtons || filterButton || exportButton || refreshButton) {
      buttons = (
        <div className={getClassName('buttons')}>
          {addButton}
          {filterButton}
          {exportButton}
          {refreshButton}
          {bulkRemoveButton}
          {actionButtons}
          {filterMenu}
        </div>
      )
    }

    return (
      <div className={getClassName(containerClasses)} style={containerStyle}>
        {buttons}
        {searchBar}
        {groupBar}
      </div>
    )
  }
}

Header.propTypes = {
  allColumns: propTypes.object.isRequired,
  groups: propTypes.arrayOf(propTypes.string).isRequired,
  sortColumn: propTypes.string,
  sortDirection: propTypes.oneOf(['asc', 'desc']),
  groupingAllowed: propTypes.bool.isRequired,
  sortingAllowed: propTypes.bool.isRequired,
  isGroupByEnabled: propTypes.bool.isRequired,
  searchingAllowed: propTypes.bool.isRequired,
  singularNoun: propTypes.string.isRequired,
  pluralNoun: propTypes.string.isRequired,
  stickyTop: propTypes.number,
  fullSize: propTypes.bool,
  onSearched: propTypes.func.isRequired,
  onSortChanged: propTypes.func.isRequired,
  onGroupsChanged: propTypes.func,
  actions: propTypes.array,
  children: propTypes.any,
  showAdd: propTypes.bool,
  search: propTypes.string,
  searchPlaceholder: propTypes.string,
  addLabel: propTypes.node,
  isAddButtonDisabled: propTypes.bool,
  onAdd: propTypes.func,
  onBulkRemove: propTypes.func,
  onExport: propTypes.func,
  onRefresh: propTypes.func,
  tightFilters: propTypes.bool,
}

Header.defaultProps = {
  showAdd: false,
  addLabel: null,
  searchPlaceholder: null,
  isAddButtonDisabled: false,
  isGroupByEnabled: true,
}

export default Header
